import {
	normalizeOrganizationProfileStatusFromApi,
	ORGANIZATION_PROFILE_STATUSES,
} from '@/config/settingsConstants'

const getOrganizationProfile = (data = {}) =>
	data?.organization_profile || data || {}

/**
 * Resolve organization profile workflow status from API payload.
 * Uses the API status field and defaults to unverified when missing.
 *
 * @param {Object} organizationData
 * @return {String}
 */
const resolveOrganizationProfileStatus = (organizationData = {}) => {
	const profile = getOrganizationProfile(organizationData)
	const status = normalizeOrganizationProfileStatusFromApi(
		profile.status || organizationData.status || ''
	)

	if (
		status &&
		Object.values(ORGANIZATION_PROFILE_STATUSES).includes(status)
	) {
		return status
	}

	return ORGANIZATION_PROFILE_STATUSES.UNVERIFIED
}

/**
 * Display name from organization GET payload (`name` on root payload).
 *
 * @param {Object} organizationData
 * @return {String}
 */
const resolveOrganizationDisplayName = (organizationData = {}) => {
	const name = organizationData.name

	if (typeof name !== 'string' || !name.trim()) {
		return ''
	}

	return name.trim()
}

/**
 * Organization attention when status is present and not verified.
 *
 * @param {String} status
 * @return {Boolean}
 */
const organizationNeedsAttention = (status = '') => {
	if (!status) {
		return false
	}

	return status !== ORGANIZATION_PROFILE_STATUSES.VERIFIED
}

/**
 * Caller ID attention when enforcement is enabled and caller ID is not verified.
 *
 * @param {Object} partner
 * @return {Boolean}
 */
const callerIdNeedsAttention = (partner = {}) => {
	if (!partner.enforce_caller_id_verification) {
		return false
	}

	const callerIdStatus = String(
		partner.ob_caller_id_verification_status || 'unverified'
	)
		.toLowerCase()
		.trim()

	return callerIdStatus !== 'verified'
}

export default {
	/**
	 * Organization profile stored from GET/PATCH organization APIs.
	 *
	 * @param {Object} state
	 * @return {?Object}
	 */
	organizationProfile: state => state.organizationProfile,

	/**
	 * Normalized organization profile status from the stored payload.
	 *
	 * @param {Object} state
	 * @return {String}
	 */
	organizationProfileStatus(state) {
		if (!state.organizationProfile) {
			return ''
		}

		return resolveOrganizationProfileStatus(state.organizationProfile)
	},

	/**
	 * Organization display name from GET/PATCH payload (`name`).
	 *
	 * @param {Object} state
	 * @return {String}
	 */
	organizationDisplayName(state) {
		if (!state.organizationProfile) {
			return ''
		}

		return resolveOrganizationDisplayName(state.organizationProfile)
	},

	/**
	 * Whether organization profile needs attention.
	 *
	 * @param {Object} state
	 * @param {Object} getters
	 * @return {Boolean}
	 */
	requiresOrganizationProfileAttention(state, getters) {
		return organizationNeedsAttention(getters.organizationProfileStatus)
	},

	/**
	 * Whether partner caller ID needs attention.
	 *
	 * @param {Object} state
	 * @param {Object} getters
	 * @param {Object} rootState
	 * @param {Object} rootGetters
	 * @return {Boolean}
	 */
	requiresCallerIdAttention(state, getters, rootState, rootGetters) {
		return callerIdNeedsAttention(rootGetters['partners/active'])
	},

	/**
	 * Whether the header should show the Attention Required pill.
	 * Org admins only see organization-profile attention, not caller ID.
	 *
	 * @param {Object} state
	 * @param {Object} getters
	 * @param {Object} rootState
	 * @param {Object} rootGetters
	 * @return {Boolean}
	 */
	requiresOrganizationAttention(state, getters, rootState, rootGetters) {
		if (rootGetters['auth/isOrganizationOnlyAdmin']) {
			return getters.requiresOrganizationProfileAttention
		}

		return (
			getters.requiresOrganizationProfileAttention ||
			getters.requiresCallerIdAttention
		)
	},
}
